import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, FilePlus, FolderOpen, RotateCcw, X } from 'lucide-react';
import { investigationService } from '../../services/api';
import { toast } from 'sonner';
import { clsx } from 'clsx';
import {
    clearWorkbenchDraft,
    hasUnfinishedWorkbenchForCase,
    readWorkbenchDraft,
} from '../../data/workbenchDraft';
import { useGraphStore } from '../../stores/useGraphStore';
import { useWorkbenchSessionStore } from '../../stores/useWorkbenchSessionStore';

export interface CaseWorkbenchEntryModalProps {
    isOpen: boolean;
    caseId: string;
    caseTitle?: string | null;
    onClose: () => void;
    /** Prefer starting blank (New Investigation / Add Address). */
    preferFresh?: boolean;
}

interface SavedInvestigation {
    id: string;
    name?: string | null;
    root_address?: string | null;
    root_chain?: string | null;
    updated_at?: string;
    graph_state?: { elements?: unknown[] } | null;
}

type Step = 'choose' | 'pick-investigation';

export function CaseWorkbenchEntryModal({
    isOpen,
    caseId,
    caseTitle,
    onClose,
    preferFresh = false,
}: CaseWorkbenchEntryModalProps) {
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>('choose');
    const [loading, setLoading] = useState(false);
    const [investigations, setInvestigations] = useState<SavedInvestigation[]>([]);
    const [hasUnfinished, setHasUnfinished] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setStep('choose');
        const canvasCount = useGraphStore.getState().elements.length;
        const sessionCaseId = useWorkbenchSessionStore.getState().caseId;
        setHasUnfinished(hasUnfinishedWorkbenchForCase(caseId, {
            canvasElementCount: canvasCount,
            sessionCaseId,
        }));

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const list = await investigationService.listByCase(caseId, false);
                if (!cancelled) setInvestigations(Array.isArray(list) ? list : []);
            } catch (e) {
                console.error('Failed to load investigations for entry modal', e);
                if (!cancelled) {
                    setInvestigations([]);
                    toast.error('Could not load saved investigations');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, caseId]);

    if (!isOpen) return null;

    const goResumeUnfinished = () => {
        const draft = readWorkbenchDraft();
        const invId = draft?.investigationId;
        const params = new URLSearchParams({ case_id: caseId, resume: '1' });
        if (invId) params.set('investigation_id', invId);
        // Keep draft so Workbench can apply canvas on resume=1.
        useWorkbenchSessionStore.getState().setCaseContext(caseId, invId || null);
        onClose();
        navigate(`/workbench?${params.toString()}`);
    };

    const goFresh = () => {
        clearWorkbenchDraft();
        useWorkbenchSessionStore.getState().setCaseContext(caseId, null);
        onClose();
        navigate(`/workbench?case_id=${encodeURIComponent(caseId)}&fresh=1`);
    };

    const goSaved = (invId: string) => {
        clearWorkbenchDraft();
        useWorkbenchSessionStore.getState().setCaseContext(caseId, invId);
        onClose();
        navigate(`/workbench?investigation_id=${encodeURIComponent(invId)}&case_id=${encodeURIComponent(caseId)}`);
    };

    const formatUpdated = (iso?: string) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleString('en-IN', {
                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            });
        } catch {
            return '';
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="case-workbench-entry-title">
            <div className="w-full max-w-lg rounded-xl flex flex-col max-h-[85vh] border border-border bg-card p-5 shadow-overlay">
                <div className="flex justify-between items-start gap-3 mb-1">
                    <div>
                        <h3 id="case-workbench-entry-title" className="text-lg font-semibold text-foreground">
                            Continue in Workbench?
                        </h3>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            {caseTitle ? (
                                <>Case: <span className="text-foreground font-medium">{caseTitle}</span></>
                            ) : (
                                'Choose how to open this case on the canvas.'
                            )}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {step === 'choose' && (
                    <div className="mt-4 space-y-2">
                        {hasUnfinished && (
                            <ChoiceButton
                                icon={RotateCcw}
                                title="Resume unfinished session"
                                blurb="Restore the canvas you left mid-investigation for this case."
                                emphasized={!preferFresh}
                                onClick={goResumeUnfinished}
                            />
                        )}
                        <ChoiceButton
                            icon={FilePlus}
                            title="Blank canvas"
                            blurb="Discard cached session and start a clean investigation under this case."
                            emphasized={preferFresh || !hasUnfinished}
                            onClick={goFresh}
                        />
                        <ChoiceButton
                            icon={FolderOpen}
                            title="Open saved investigation"
                            blurb={
                                loading
                                    ? 'Loading snapshots…'
                                    : investigations.length === 0
                                        ? 'No saved snapshots yet — use Blank canvas first.'
                                        : `${investigations.length} snapshot${investigations.length === 1 ? '' : 's'} on this case.`
                            }
                            disabled={loading || investigations.length === 0}
                            onClick={() => setStep('pick-investigation')}
                        />
                    </div>
                )}

                {step === 'pick-investigation' && (
                    <div className="mt-4 flex flex-col min-h-0 flex-1">
                        <button
                            type="button"
                            onClick={() => setStep('choose')}
                            className="text-xs text-primary hover:underline self-start mb-2"
                        >
                            ← Back to choices
                        </button>
                        <div className="flex-1 overflow-y-auto space-y-2 max-h-[50vh] pr-1">
                            {investigations.map((inv) => (
                                <button
                                    key={inv.id}
                                    type="button"
                                    onClick={() => goSaved(inv.id)}
                                    className="w-full text-left p-3 border border-border rounded-md hover:border-primary hover:bg-primary/10 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <Activity className="w-4 h-4 text-primary shrink-0" />
                                        <span className="font-medium text-sm text-foreground truncate">
                                            {inv.name || 'Untitled investigation'}
                                        </span>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1 font-mono truncate">
                                        {[inv.root_chain, inv.root_address].filter(Boolean).join(' · ') || 'No root address'}
                                        {inv.updated_at ? ` · ${formatUpdated(inv.updated_at)}` : ''}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex justify-end pt-4 mt-2 border-t border-border">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

function ChoiceButton({
    icon: Icon,
    title,
    blurb,
    onClick,
    emphasized,
    disabled,
}: {
    icon: typeof RotateCcw;
    title: string;
    blurb: string;
    onClick: () => void;
    emphasized?: boolean;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={clsx(
                'w-full text-left p-3 rounded-md border transition-colors flex gap-3 items-start',
                disabled && 'opacity-50 cursor-not-allowed',
                !disabled && emphasized && 'border-primary/40 bg-primary/10 hover:bg-primary/15',
                !disabled && !emphasized && 'border-border hover:border-primary/30 hover:bg-muted/50',
            )}
        >
            <span className={clsx('mt-0.5 p-1.5 rounded-md shrink-0', emphasized ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground')}>
                <Icon className="w-4 h-4" aria-hidden />
            </span>
            <span>
                <span className="block text-sm font-medium text-foreground">{title}</span>
                <span className="block text-xs text-muted-foreground mt-0.5 leading-relaxed">{blurb}</span>
            </span>
        </button>
    );
}
